import { useState, useEffect } from "react";
import { useLocation, useParams } from "wouter";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Building,
  ChevronDown,
  LayoutDashboard,
  Package,
  FileUp,
  Users,
  LogOut,
  Menu,
  X
} from "lucide-react";
import { TendererSidebar } from "./TendererSidebar";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

interface TendererAuth {
  email: string;
  accessCode: string;
  companyName: string;
  authenticatedAt: string;
}

interface Project {
  id: string;
  name: string;
  packageCount: number;
}

interface TendererLayoutProps {
  children: React.ReactNode;
  authData: TendererAuth;
}

export function TendererLayout({ children, authData }: TendererLayoutProps) {
  const [, setLocation] = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const params = useParams<{ companyCode?: string; projectId?: string }>() || {};

  // Fetch projects this tenderer has access to based on tender submissions
  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['/api/tenderer/accessible-projects', authData?.email, authData?.accessCode],
    queryFn: () => apiRequest(`/api/tenderer/accessible-projects?email=${encodeURIComponent(authData?.email || '')}&accessCode=${encodeURIComponent(authData?.accessCode || '')}`, {
      method: 'GET'
    }),
    enabled: !!authData?.email && !!authData?.accessCode,
  });

  // Set first project as default if none selected, or find project from URL params
  useEffect(() => {
    if (projects.length > 0) {
      if (params.projectId) {
        // Find project by ID from URL params
        const projectFromUrl = projects.find(p => p.id === params.projectId);
        if (projectFromUrl && (!selectedProject || selectedProject.id !== projectFromUrl.id)) {
          setSelectedProject(projectFromUrl);
        }
      } else if (!selectedProject) {
        // Default to first project if none selected and navigate to unified URL
        setSelectedProject(projects[0]);
        const companyCode = authData.accessCode;
        const currentLocation = window.location.pathname;
        
        // Only redirect if we're not already on a valid unified URL
        if (!currentLocation.includes(`/dashboard/${projects[0].id}/tenderer/${companyCode}`)) {
          setLocation(`/dashboard/${projects[0].id}/tenderer/${companyCode}`);
        }
      }
    }
  }, [projects, selectedProject, params.projectId, authData.accessCode, setLocation]);

  const handleLogout = () => {
    sessionStorage.removeItem('tendererAuth');
    setLocation('/tenderer');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const getCompanyInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gray-100">
      {/* Sidebar */}
      <TendererSidebar 
        open={sidebarOpen} 
        selectedProject={selectedProject}
        authData={authData}
        toggleSidebar={toggleSidebar}
      />
      
      {/* Main Content */}
      <div className={`w-full h-full flex flex-col min-w-0 flex-1 transition-all duration-300 ${sidebarOpen ? 'md:ml-64' : 'md:ml-16'}`}>
        {/* Fixed Header */}
        <div className={`fixed top-0 right-0 left-0 bg-white border-b border-gray-200 shadow-sm z-40 transition-all duration-300 ${sidebarOpen ? 'md:left-64' : 'md:left-16'}`}>
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14">
              {/* Left side - Mobile menu + Project switcher */}
              <div className="flex items-center space-x-2">
                {/* Mobile menu button */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="md:hidden"
                  onClick={toggleSidebar}
                >
                  <Menu className="h-5 w-5" />
                </Button>

                {/* Project Switcher - moved closer to left and center-aligned with sidebar title */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="flex items-center space-x-2 min-w-[200px] ml-2">
                      <Building className="h-4 w-4" />
                      <span className="truncate">
                        {selectedProject?.name || "Select Project"}
                      </span>
                      <ChevronDown className="h-4 w-4 ml-auto" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[300px]">
                    <DropdownMenuLabel>Available Projects</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {projects.map((project) => (
                      <DropdownMenuItem
                        key={project.id}
                        onClick={() => {
                          setSelectedProject(project);
                          // Navigate to unified URL structure when project is switched
                          const companyCode = authData.accessCode;
                          const currentLocation = window.location.pathname;
                          let basePage = '/packages'; // Default to packages tab
                          
                          if (currentLocation.includes('/returns/')) {
                            basePage = '/returns';
                          } else if (currentLocation.includes('/members/')) {
                            basePage = '/members';
                          }
                          
                          const newPath = `${basePage}/${project.id}/tenderer/${companyCode}`;
                          setLocation(newPath);
                        }}
                        className="flex items-center justify-between"
                      >
                        <div>
                          <div className="font-medium">{project.name}</div>
                          <div className="text-sm text-gray-500">
                            {project.packageCount} package{project.packageCount !== 1 ? 's' : ''}
                          </div>
                        </div>
                        {selectedProject?.id === project.id && (
                          <div className="w-2 h-2 bg-blue-600 rounded-full" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Right side - User menu */}
              <div className="flex items-center space-x-4">
                {/* Company info */}
                <div className="hidden sm:block text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {authData.companyName}
                  </div>
                  <div className="text-xs text-gray-500">
                    {authData.email}
                  </div>
                </div>

                {/* User menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-blue-600 text-white">
                          {getCompanyInitials(authData.companyName)}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" forceMount>
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">
                          {authData.companyName}
                        </p>
                        <p className="text-xs leading-none text-muted-foreground">
                          {authData.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Log out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area with top padding for fixed header */}
        <main className="flex-1 h-screen overflow-y-auto pt-16">
          {children}
        </main>
      </div>
    </div>
  );
}